import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import HobbyEmptyState from '../../components/common/HobbyEmptyState';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  ADD_MOVIE,
  DELETE_MOVIE,
  GET_MOVIE,
  GET_MOVIES,
  IMPORT_LETTERBOXD_MOVIES,
  PREVIEW_LETTERBOXD_IMPORT,
  SEARCH_MOVIES,
  UPDATE_MOVIE,
} from './operations';
import MovieForm from './MoviesForm';
import MovieAddSearch from './MovieAddSearch';
import LetterboxdImportModal from './LetterboxdImportModal';
import PremiumUpsellModal from '../../components/PremiumUpsellModal';
import DefaultMediaFallback from '../../components/common/DefaultMediaFallback';
import ModuleListSkeleton from '../../components/common/ModuleListSkeleton';
import DetailActionMenu from '../../components/common/DetailActionMenu';
import RatingReadout from '../../components/common/RatingReadout';
import { GET_MY_SUBSCRIPTION_STATUS } from '../../views/account/operations';
import { getPublicErrorMessage, isLimitReachedError } from '../../utils/errors';
import { canCreateEntries, getHobbyUsageLimit, getRemainingEntries, hasPremiumForHobby } from '../../utils/subscriptionLimits';
import { mediaStrings, moduleCommonStrings } from '../../content/hobbyModules';
import { shareHighlightStrings } from '../../content/shareHighlights';
import { buildShareHighlight, openShareHighlight } from '../shareHighlights/shareHighlight';

const MOVIES_PAGE_SIZE = 20;

const defaultMovie = {
  releaseYear: '',
  posterUrl: '',
  director: '',
  cast: [],
  genres: [],
  runtime: '',
  status: 'Want to watch',
  rating: '',
  favorite: false,
  watchedDate: '',
  rewatch: false,
  review: '',
  notes: '',
  spoiler: false,
  collection: 'My Movies',
  whereWatched: '',
};

/**
 * Performs the split csv operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function splitCsv(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * Normalizes movie.
 *
 * @param {*} movie - movie value.
 * @returns {*} The derived operation result.
 */
function parseMovie(movie) {
  return {
    ...defaultMovie,
    ...movie,
    cast: Array.isArray(movie.cast) ? movie.cast : splitCsv(movie.cast),
    genres: Array.isArray(movie.genres) ? movie.genres : splitCsv(movie.genres),
    collection: movie.collection || defaultMovie.collection,
    status: movie.status || defaultMovie.status,
    favorite: Boolean(movie.favorite),
    rewatch: Boolean(movie.rewatch),
    spoiler: Boolean(movie.spoiler),
  };
}

/**
 * Performs the movie from recommendation operation.
 *
 * @param {*} recommendation - recommendation value.
 * @returns {*} The derived operation result.
 */
function movieFromRecommendation(recommendation) {
  const payload = recommendation?.actionPayload || {};
  return {
    tmdbId: payload.tmdbId || recommendation?.externalId || '',
    title: payload.title || recommendation?.title || '',
    releaseYear: payload.releaseYear || '',
    posterUrl: payload.posterUrl || recommendation?.media?.url || '',
    director: payload.director || '',
    cast: Array.isArray(payload.cast) ? payload.cast : [],
    genres: Array.isArray(payload.genres) ? payload.genres : [],
    runtime: payload.runtime || '',
    overview: payload.overview || recommendation?.body || '',
    status: 'Want to watch',
    rating: '',
    favorite: false,
    watchedDate: '',
    rewatch: false,
    review: '',
    notes: '',
    spoiler: false,
    collection: defaultMovie.collection,
    whereWatched: '',
  };
}

/**
 * Performs the unique values operation.
 *
 * @param {Array<*>} items - items collection.
 * @param {*} getter - getter value.
 * @returns {*} The derived operation result.
 */
function uniqueValues(items, getter) {
  return Array.from(new Set(items.flatMap(getter).filter(Boolean))).sort();
}

/**
 * Performs the count by collection operation.
 *
 * @param {*} movies - movies value.
 * @param {*} collection - collection value.
 * @returns {*} The derived operation result.
 */
function countByCollection(movies, collection) {
  return movies.filter((movie) => movie.collection === collection).length;
}

/**
 * Normalizes runtime minutes.
 *
 * @param {*} runtime - runtime value.
 * @returns {*} The derived operation result.
 */
function parseRuntimeMinutes(runtime) {
  const match = String(runtime || '').match(/\d+/);
  return match ? Number(match[0]) : 0;
}

/**
 * Performs the watched date key operation.
 *
 * @param {*} movie - movie value.
 * @returns {*} The derived operation result.
 */
function watchedDateKey(movie) {
  if (!movie.watchedDate) return null;
  const [year, month] = movie.watchedDate.split('-');
  if (!year || !month) return null;
  return { year, month: `${year}-${month}` };
}

/**
 * Performs the count items operation.
 *
 * @param {Array<*>} items - items collection.
 * @param {*} getter - getter value.
 * @returns {*} The derived operation result.
 */
function countItems(items, getter) {
  const counts = new Map();

  items.forEach((item) => {
    getter(item).filter(Boolean).forEach((value) => {
      counts.set(value, (counts.get(value) || 0) + 1);
    });
  });

  return Array.from(counts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

/**
 * Normalizes hours.
 *
 * @param {*} minutes - minutes value.
 * @returns {*} The derived operation result.
 */
function formatHours(minutes) {
  if (!minutes) return '0h';
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

/**
 * Creates movie stats.
 *
 * @param {*} movies - movies value.
 * @returns {*} The derived operation result.
 */
function buildMovieStats(movies) {
  const watchedMovies = movies.filter((movie) => (
    movie.watchedDate || movie.status === 'Watched' || movie.rewatch
  ));
  const datedWatchedMovies = watchedMovies.filter((movie) => watchedDateKey(movie));
  const yearCounts = countItems(datedWatchedMovies, (movie) => [watchedDateKey(movie).year]);
  const monthCounts = countItems(datedWatchedMovies, (movie) => [watchedDateKey(movie).month]);
  const ratingDistribution = countItems(
    watchedMovies.filter((movie) => movie.rating),
    (movie) => [`${Number(movie.rating).toFixed(1)}`]
  ).sort((a, b) => Number(b.label) - Number(a.label));
  const topGenres = countItems(watchedMovies, (movie) => movie.genres).slice(0, 6);
  const topDirectors = countItems(watchedMovies, (movie) => splitCsv(movie.director)).slice(0, 6);
  const favoriteMovies = movies.filter((movie) => movie.favorite);
  const totalRuntime = watchedMovies.reduce((total, movie) => total + parseRuntimeMinutes(movie.runtime), 0);
  const averageRating = watchedMovies.filter((movie) => movie.rating).reduce((total, movie, _, ratedMovies) => (
    total + Number(movie.rating) / ratedMovies.length
  ), 0);

  return {
    watchedMovies,
    datedWatchedMovies,
    yearCounts,
    monthCounts: monthCounts.slice(0, 12),
    ratingDistribution,
    topGenres,
    topDirectors,
    favoriteMovies,
    totalRuntime,
    averageRating,
  };
}

/**
 * Renders the movie stats view experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.movies - movies value.
 * @param {*} props.stats - stats value.
 * @returns {JSX.Element} The rendered interface.
 */
function MovieStatsView({ movies, stats }) {
  const maxYearCount = Math.max(...stats.yearCounts.map((item) => item.count), 1);
  const maxMonthCount = Math.max(...stats.monthCounts.map((item) => item.count), 1);
  const maxRatingCount = Math.max(...stats.ratingDistribution.map((item) => item.count), 1);

  return (
    <section className="movie-stats-view">
      <div className="movie-stats-summary">
        <article>
          <span>{stats.watchedMovies.length}</span>
          <p>Watched</p>
        </article>
        <article>
          <span>{formatHours(stats.totalRuntime)}</span>
          <p>Total runtime</p>
        </article>
        <article>
          <span>{stats.averageRating ? <RatingReadout rating={stats.averageRating} /> : '-'}</span>
          <p>Avg rating</p>
        </article>
        <article>
          <span>{stats.favoriteMovies.length}</span>
          <p>Favorites</p>
        </article>
      </div>

      <div className="movie-stats-grid">
        <section className="movie-stat-panel">
          <h3>Yearly watch count</h3>
          {stats.yearCounts.length ? (
            <div className="stat-bar-list">
              {stats.yearCounts.map((item) => (
                <div className="stat-bar-row" key={item.label}>
                  <span>{item.label}</span>
                  <div><i style={{ width: `${(item.count / maxYearCount) * 100}%` }} /></div>
                  <strong>{item.count}</strong>
                </div>
              ))}
            </div>
          ) : <p className="muted-copy">Add watched dates to see yearly trends.</p>}
        </section>

        <section className="movie-stat-panel">
          <h3>Monthly watch count</h3>
          {stats.monthCounts.length ? (
            <div className="stat-bar-list">
              {stats.monthCounts.map((item) => (
                <div className="stat-bar-row" key={item.label}>
                  <span>{item.label}</span>
                  <div><i style={{ width: `${(item.count / maxMonthCount) * 100}%` }} /></div>
                  <strong>{item.count}</strong>
                </div>
              ))}
            </div>
          ) : <p className="muted-copy">Monthly counts appear once movies have watched dates.</p>}
        </section>

        <section className="movie-stat-panel">
          <h3>Rating distribution</h3>
          {stats.ratingDistribution.length ? (
            <div className="stat-bar-list">
              {stats.ratingDistribution.map((item) => (
                <div className="stat-bar-row" key={item.label}>
                  <span>{item.label} stars</span>
                  <div><i style={{ width: `${(item.count / maxRatingCount) * 100}%` }} /></div>
                  <strong>{item.count}</strong>
                </div>
              ))}
            </div>
          ) : <p className="muted-copy">Rate watched movies to build a distribution.</p>}
        </section>

        <section className="movie-stat-panel">
          <h3>Top genres</h3>
          {stats.topGenres.length ? (
            <div className="stat-chip-list">
              {stats.topGenres.map((item) => <span key={item.label}>{item.label} <strong>{item.count}</strong></span>)}
            </div>
          ) : <p className="muted-copy">Genres will show here after watched movies are tagged.</p>}
        </section>

        <section className="movie-stat-panel">
          <h3>Top directors</h3>
          {stats.topDirectors.length ? (
            <div className="stat-chip-list">
              {stats.topDirectors.map((item) => <span key={item.label}>{item.label} <strong>{item.count}</strong></span>)}
            </div>
          ) : <p className="muted-copy">Directors will show here after watched movies are added.</p>}
        </section>

        <section className="movie-stat-panel">
          <h3>Favorites recap</h3>
          {stats.favoriteMovies.length ? (
            <div className="favorite-recap-list">
              {stats.favoriteMovies.slice(0, 5).map((movie) => (
                <div key={movie.id}>
                  <strong>{movie.title}</strong>
                  <span>{movie.releaseYear}</span>
                  {movie.rating && <RatingReadout rating={movie.rating} />}
                </div>
              ))}
            </div>
          ) : <p className="muted-copy">Mark movies as favorites to build a recap.</p>}
        </section>
      </div>

      {movies.length === 0 && (
        <div className="empty-state">
          <p>Add a few movies and this stats view will start filling in.</p>
        </div>
      )}
    </section>
  );
}

/** Owns the Movies library, search/import flows, detail routing, filters, and mutations. */
export default function MoviesModule({
  isAdding,
  setIsAdding,
  theme,
  onClose,
  onRegisterCloseGuard,
  onHeaderActionsChange,
  onHeaderTitleChange,
  modulePath = '',
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const targetItemId = searchParams.get('itemId');
  const openedTargetRef = useRef(null);
  const openedRecommendationRef = useRef(null);
  const [importedMovie, setImportedMovie] = useState(null);
  const [manualMovieEntry, setManualMovieEntry] = useState(false);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [editingMovie, setEditingMovie] = useState(null);
  const [moviePendingDelete, setMoviePendingDelete] = useState(null);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [search, setSearch] = useState('');
  const [activeCollection, setActiveCollection] = useState('');
  const [activeStatus, setActiveStatus] = useState('');
  const [activeGenre, setActiveGenre] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [showLetterboxdImport, setShowLetterboxdImport] = useState(false);
  const [letterboxdImportResult, setLetterboxdImportResult] = useState(null);
  const [showPremiumUpsell, setShowPremiumUpsell] = useState(false);
  const routeParts = useMemo(
    () => String(modulePath || '').split('/').map((part) => decodeURIComponent(part)).filter(Boolean),
    [modulePath]
  );
  const isCreateRoute = routeParts[0] === 'new';
  const routeMovieId = routeParts[0] && routeParts[0] !== 'new' ? routeParts[0] : '';
  const isEditRoute = Boolean(routeMovieId && routeParts[1] === 'edit');
  const isAddFlow = isAdding || isCreateRoute;
  const subscriptionRefetch = [{ query: GET_MY_SUBSCRIPTION_STATUS }];
  const { data: subscriptionData, loading: subscriptionLoading, error: subscriptionError, refetch: refetchSubscription } = useQuery(GET_MY_SUBSCRIPTION_STATUS, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const subscription = subscriptionData?.getMySubscriptionStatus;
  const importUsageReady = !subscriptionLoading && !subscriptionError && Boolean(subscription)
    && (hasPremiumForHobby(subscription, 'movies') || getHobbyUsageLimit(subscription, 'movies')?.remaining != null);

  const { loading, error, data, fetchMore } = useQuery(GET_MOVIES, {
    variables: { first: MOVIES_PAGE_SIZE, after: null },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const [searchMovies, {
    data: movieSearchData,
    loading: isSearchingMovies,
    error: movieSearchError,
  }] = useLazyQuery(SEARCH_MOVIES);
  const [loadMovieDetail] = useLazyQuery(GET_MOVIE, { fetchPolicy: 'network-only' });
  const parsedMovies = useMemo(() => (data?.getMoviesPage?.nodes || []).map(parseMovie), [data]);
  const moviesPageInfo = data?.getMoviesPage?.pageInfo || {};

  const collections = useMemo(
    () => uniqueValues(parsedMovies, (movie) => [movie.collection]),
    [parsedMovies]
  );
  const statuses = useMemo(
    () => uniqueValues(parsedMovies, (movie) => [movie.status]),
    [parsedMovies]
  );
  const genres = useMemo(
    () => uniqueValues(parsedMovies, (movie) => movie.genres),
    [parsedMovies]
  );

  const filteredMovies = useMemo(() => {
    const query = search.trim().toLowerCase();

    return parsedMovies.filter((movie) => {
      const searchable = [
        movie.title,
        movie.releaseYear,
        movie.director,
        movie.collection,
        movie.whereWatched,
        movie.status,
        movie.cast.join(' '),
        movie.genres.join(' '),
        movie.review,
        movie.notes,
      ].join(' ').toLowerCase();

      return (
        (!query || searchable.includes(query)) &&
        (!activeCollection || movie.collection === activeCollection) &&
        (!activeStatus || movie.status === activeStatus) &&
        (!activeGenre || movie.genres.includes(activeGenre)) &&
        (!favoritesOnly || movie.favorite)
      );
    });
  }, [activeCollection, activeGenre, activeStatus, favoritesOnly, parsedMovies, search]);

  const [addMovie, { loading: isSaving }] = useMutation(ADD_MOVIE, {
    refetchQueries: [{ query: GET_MOVIES, variables: { first: MOVIES_PAGE_SIZE, after: null } }, ...subscriptionRefetch],
    onCompleted: () => {
      setImportedMovie(null);
      setHasFormChanges(false);
      onClose();
    },
  });

  const [updateMovie, { loading: isUpdating }] = useMutation(UPDATE_MOVIE, {
    refetchQueries: [{ query: GET_MOVIES, variables: { first: MOVIES_PAGE_SIZE, after: null } }],
    onCompleted: (result) => {
      setEditingMovie(null);
      setHasFormChanges(false);
      setSelectedMovie(parseMovie(result.updateMovie));
    },
  });

  const [deleteMovie, { loading: isDeleting }] = useMutation(DELETE_MOVIE, {
    refetchQueries: [{ query: GET_MOVIES, variables: { first: MOVIES_PAGE_SIZE, after: null } }, ...subscriptionRefetch],
    onCompleted: () => {
      setMoviePendingDelete(null);
      setSelectedMovie(null);
      navigate('/hobby/movies');
    },
  });

  const [previewLetterboxdImport, {
    data: letterboxdPreviewData,
    loading: isPreviewingLetterboxd,
    error: letterboxdPreviewError,
    reset: resetLetterboxdPreview,
  }] = useMutation(PREVIEW_LETTERBOXD_IMPORT);

  const [importLetterboxdMovies, {
    loading: isImportingLetterboxd,
    error: letterboxdImportError,
    reset: resetLetterboxdImport,
  }] = useMutation(IMPORT_LETTERBOXD_MOVIES, {
    refetchQueries: [{ query: GET_MOVIES, variables: { first: MOVIES_PAGE_SIZE, after: null } }, ...subscriptionRefetch],
    onCompleted: (result) => {
      setLetterboxdImportResult(result.importLetterboxdMovies);
    },
  });

  /**
   * Normalizes input.
   *
   * @param {*} formData - form data value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const cleanInput = (formData) => ({
    title: formData.title.trim(),
    releaseYear: formData.releaseYear.trim(),
    posterUrl: formData.posterUrl.trim(),
    director: formData.director.trim(),
    cast: splitCsv(formData.cast),
    genres: splitCsv(formData.genres),
    runtime: formData.runtime.trim(),
    status: formData.status,
    rating: formData.rating,
    favorite: Boolean(formData.favorite),
    watchedDate: formData.status === 'Watched' ? formData.watchedDate : '',
    rewatch: false,
    review: formData.review.trim(),
    notes: '',
    spoiler: Boolean(formData.spoiler),
    collection: formData.collection.trim() || defaultMovie.collection,
    whereWatched: formData.whereWatched.trim(),
  });

  /**
   * Handles save.
   *
   * @param {*} formData - form data value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSave = async (formData) => {
    const input = cleanInput(formData);

    try {
      if (formData.id) {
        await updateMovie({ variables: { id: formData.id, input } });
        navigate(`/hobby/movies/${encodeURIComponent(formData.id)}`);
        return;
      }

      await addMovie({ variables: { input } });
      navigate('/hobby/movies');
    } catch (error) {
      if (isLimitReachedError(error)) {
        setShowPremiumUpsell(true);
        return;
      }
      alert(getPublicErrorMessage(error));
    }
  };

  const closeForm = useCallback(() => {
    const editingMovieId = editingMovie?.id;
    setImportedMovie(null);
    setManualMovieEntry(false);
    setEditingMovie(null);
    setHasFormChanges(false);
    navigate(editingMovieId ? `/hobby/movies/${encodeURIComponent(editingMovieId)}` : '/hobby/movies');
    onClose();
  }, [editingMovie?.id, navigate, onClose]);

  const handleCancelForm = useCallback(({ confirmDiscard } = {}) => {
    if (confirmDiscard && hasFormChanges) {
      const shouldDiscard = window.confirm('Discard your unsaved movie changes?');
      if (!shouldDiscard) return;
    }

    closeForm();
  }, [closeForm, hasFormChanges]);

  /**
   * Handles confirm delete.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleConfirmDelete = async () => {
    if (!moviePendingDelete) return;
    await deleteMovie({ variables: { id: moviePendingDelete.id } });
  };

  /**
   * Handles load more.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleLoadMore = async () => {
    if (!moviesPageInfo.hasNextPage || !moviesPageInfo.endCursor) return;
    await fetchMore({
      variables: {
        first: MOVIES_PAGE_SIZE,
        after: moviesPageInfo.endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.getMoviesPage) return previousResult;
        return {
          getMoviesPage: {
            ...fetchMoreResult.getMoviesPage,
            nodes: [
              ...(previousResult.getMoviesPage?.nodes || []),
              ...(fetchMoreResult.getMoviesPage.nodes || []),
            ],
          },
        };
      },
    });
  };

  const handleSearchMovies = useCallback((query) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;
    searchMovies({ variables: { query: trimmedQuery } });
  }, [searchMovies]);

  /**
   * Handles use imported movie.
   *
   * @param {*} movie - movie value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleUseImportedMovie = (movie) => {
    if (!canCreateEntries(subscription, 'movies')) {
      setShowPremiumUpsell(true);
      return;
    }
    setImportedMovie({
      title: movie.title,
      releaseYear: movie.releaseYear || '',
      posterUrl: movie.posterUrl || '',
      director: movie.director || '',
      cast: movie.cast || [],
      genres: movie.genres || [],
      runtime: movie.runtime || '',
      status: 'Want to watch',
      rating: '',
      favorite: false,
      watchedDate: '',
      rewatch: false,
      review: '',
      notes: '',
      spoiler: false,
      collection: 'My Movies',
      whereWatched: '',
    });
    setManualMovieEntry(false);
    setIsAdding(true);
    navigate('/hobby/movies/new');
  };

  /**
   * Handles start add.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleStartAdd = () => {
    if (!canCreateEntries(subscription, 'movies')) {
      setShowPremiumUpsell(true);
      return;
    }
    setImportedMovie(null);
    setManualMovieEntry(false);
    setIsAdding(true);
    navigate('/hobby/movies/new');
  };

  /**
   * Performs the reset letterboxd flow operation.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const resetLetterboxdFlow = () => {
    setLetterboxdImportResult(null);
    resetLetterboxdPreview();
    resetLetterboxdImport();
  };

  /**
   * Handles open letterboxd import.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleOpenLetterboxdImport = () => {
    resetLetterboxdFlow();
    setShowLetterboxdImport(true);
    void refetchSubscription().catch(() => {});
  };

  useEffect(() => {
    if (isAddFlow || editingMovie) {
      onHeaderActionsChange?.(null);
      return () => onHeaderActionsChange?.(null);
    }

    if (selectedMovie) {
      onHeaderActionsChange?.(
        <DetailActionMenu
          label="Movie actions"
          primaryAction={{ label: 'Edit', onSelect: () => handleEditMovie(selectedMovie) }}
          actions={[
            { label: shareHighlightStrings.action, onSelect: () => openShareHighlight(buildShareHighlight('movies', selectedMovie)) },
            { label: mediaStrings.movies.deleteAction, variant: 'danger', onSelect: () => setMoviePendingDelete(selectedMovie) },
          ]}
        />
      );
      return () => onHeaderActionsChange?.(null);
    }

    onHeaderActionsChange?.(
      <div className="recipe-actions">
        <button className="btn-secondary" type="button" onClick={handleOpenLetterboxdImport}>
          Import from Letterboxd
        </button>
        <button className="btn-primary" type="button" onClick={handleStartAdd}>
          Add movie
        </button>
      </div>
    );
    return () => onHeaderActionsChange?.(null);
  }, [editingMovie, isAddFlow, onHeaderActionsChange, selectedMovie, subscription]);

  useEffect(() => {
    if (isEditRoute || editingMovie) {
      onHeaderTitleChange?.(mediaStrings.movies.form.editTitle);
    } else if (isCreateRoute || isAdding) {
      onHeaderTitleChange?.('Add movie');
    } else if (routeMovieId || selectedMovie) {
      onHeaderTitleChange?.(selectedMovie?.title || 'Movie');
    } else {
      onHeaderTitleChange?.('');
    }

    return () => onHeaderTitleChange?.('');
  }, [editingMovie, isAdding, isCreateRoute, isEditRoute, onHeaderTitleChange, routeMovieId, selectedMovie]);

  /**
   * Handles close letterboxd import.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleCloseLetterboxdImport = () => {
    setShowLetterboxdImport(false);
    resetLetterboxdFlow();
  };

  /**
   * Handles preview letterboxd.
   *
   * @param {*} input - Values supplied to the operation.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handlePreviewLetterboxd = async (input) => {
    setLetterboxdImportResult(null);
    const result = await previewLetterboxdImport({ variables: { input } });
    return result.data?.previewLetterboxdImport;
  };

  /**
   * Handles import letterboxd.
   *
   * @param {*} input - Values supplied to the operation.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleImportLetterboxd = async (input) => {
    if (!canCreateEntries(subscription, 'movies', input.selectedRowIds?.length || 1)) {
      setShowPremiumUpsell(true);
      return false;
    }
    try {
      const result = await importLetterboxdMovies({ variables: { input } });
      return result.data?.importLetterboxdMovies;
    } catch (error) {
      if (isLimitReachedError(error)) {
        setShowPremiumUpsell(true);
        return false;
      }
      throw error;
    }
  };

  /**
   * Renders premium upsell.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const renderPremiumUpsell = () => showPremiumUpsell && (
    <PremiumUpsellModal
      hobbyId="movies"
      subscription={subscription}
      onClose={() => setShowPremiumUpsell(false)}
      onOpenAccount={() => {
        setShowPremiumUpsell(false);
        window.location.assign('/account');
      }}
    />
  );

  const fetchMovieDetail = useCallback(async (movie) => {
    const result = await loadMovieDetail({ variables: { id: movie.id } });
    return parseMovie(result.data?.getMovie || movie);
  }, [loadMovieDetail]);

  /**
   * Handles select movie.
   *
   * @param {*} movie - movie value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleSelectMovie = (movie) => {
    setSelectedMovie(movie);
    navigate(`/hobby/movies/${encodeURIComponent(movie.id)}`);
  };

  /**
   * Handles edit movie.
   *
   * @param {*} movie - movie value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleEditMovie = async (movie) => {
    navigate(`/hobby/movies/${encodeURIComponent(movie.id)}/edit`);
    setEditingMovie(await fetchMovieDetail(movie));
  };

  useEffect(() => {
    if (isCreateRoute) {
      setSelectedMovie(null);
      setEditingMovie(null);
      setIsAdding(true);
      const recommendation = location.state?.homeRecommendation;
      const recommendationKey = recommendation?.id || `${recommendation?.provider || ''}:${recommendation?.externalId || ''}:${recommendation?.title || ''}`;
      if (recommendation?.actionType === 'add_movie_to_watchlist' && openedRecommendationRef.current !== recommendationKey) {
        openedRecommendationRef.current = recommendationKey;
        if (!canCreateEntries(subscription, 'movies')) {
          setShowPremiumUpsell(true);
          navigate('/hobby/movies/new', { replace: true, state: null });
          return;
        }
        setImportedMovie(movieFromRecommendation(recommendation));
        setManualMovieEntry(false);
        navigate('/hobby/movies/new', { replace: true, state: null });
      }
      return;
    }

    if (!routeMovieId) {
      setSelectedMovie(null);
      setEditingMovie(null);
      setImportedMovie(null);
      setManualMovieEntry(false);
      setIsAdding(false);
      return;
    }

    setIsAdding(false);
    const movie = parsedMovies.find((item) => item.id === routeMovieId) || { id: routeMovieId };
    fetchMovieDetail(movie)
      .then((detail) => {
        if (isEditRoute) {
          setSelectedMovie(null);
          setEditingMovie(detail);
          return;
        }
        setEditingMovie(null);
        setSelectedMovie(detail);
      })
      .catch(() => navigate('/hobby/movies', { replace: true }));
  }, [fetchMovieDetail, isCreateRoute, isEditRoute, location.state, navigate, parsedMovies, routeMovieId, setIsAdding, subscription]);

  useEffect(() => {
    if (!targetItemId || openedTargetRef.current === targetItemId) return;
    openedTargetRef.current = targetItemId;
    navigate(`/hobby/movies/${encodeURIComponent(targetItemId)}`, { replace: true });
  }, [navigate, targetItemId]);

  useEffect(() => {
    if (isAddFlow || editingMovie) {
      onRegisterCloseGuard?.(() => handleCancelForm({ confirmDiscard: true }));
      return () => onRegisterCloseGuard?.(null);
    }

    if (selectedMovie) {
      onRegisterCloseGuard?.(() => navigate('/hobby/movies'));
      return () => onRegisterCloseGuard?.(null);
    }

    onRegisterCloseGuard?.(null);
    return undefined;
  }, [editingMovie, handleCancelForm, isAddFlow, navigate, onRegisterCloseGuard, selectedMovie]);

  if (isAddFlow && !editingMovie && !importedMovie && !manualMovieEntry) {
    return (
      <MovieAddSearch
        results={movieSearchData?.searchMovies || []}
        onCancel={handleCancelForm}
        onManualEntry={() => {
          if (!canCreateEntries(subscription, 'movies')) {
            setShowPremiumUpsell(true);
            return;
          }
          setManualMovieEntry(true);
        }}
        onSearch={handleSearchMovies}
        onUseMovie={handleUseImportedMovie}
        isSearching={isSearchingMovies}
        error={movieSearchError}
      />
    );
  }

  if (isAddFlow || editingMovie) {
    return (
      <>
        <MovieForm
          theme={theme}
          onCancel={handleCancelForm}
          onSave={handleSave}
          isSaving={isSaving || isUpdating}
          initialData={editingMovie || importedMovie}
          onDirtyChange={setHasFormChanges}
          watchlists={collections}
          isEditing={Boolean(editingMovie)}
        />
        {renderPremiumUpsell()}
      </>
    );
  }

  if (selectedMovie) {
    const heroFacts = [
      selectedMovie.releaseYear && [mediaStrings.movies.form.fields.releaseYear, selectedMovie.releaseYear],
      selectedMovie.runtime && [mediaStrings.movies.form.fields.runtime, selectedMovie.runtime],
      selectedMovie.rating && [mediaStrings.movies.form.fields.rating, <RatingReadout rating={selectedMovie.rating} />],
    ].filter(Boolean);
    const libraryRows = [
      [mediaStrings.library.status, selectedMovie.status],
      [mediaStrings.movies.form.fields.collection, selectedMovie.collection],
      [selectedMovie.status === 'Watched' || selectedMovie.status === 'Abandoned' ? mediaStrings.movies.form.fields.watchedOn : mediaStrings.movies.form.fields.watchOn, selectedMovie.whereWatched],
      [mediaStrings.movies.form.fields.watchedDate, selectedMovie.status === 'Watched' ? selectedMovie.watchedDate : ''],
      [mediaStrings.library.favorites, selectedMovie.favorite ? mediaStrings.library.favorites : ''],
    ];
    const detailRows = [
      [mediaStrings.movies.form.fields.director, selectedMovie.director],
      [mediaStrings.movies.form.fields.cast, selectedMovie.cast.join(', ')],
      [mediaStrings.movies.form.fields.genres, selectedMovie.genres.join(', ')],
    ];

    return (
      <div className="module-wrapper movies-module">
        <div className="recipe-detail-view movie-detail-view">
          <button type="button" className="recipe-back-link" onClick={() => navigate('/hobby/movies')}>← Back</button>

          {moviePendingDelete && (
            <div className="modal-overlay">
              <div className="modal-content delete-confirmation">
                <p className="eyebrow">{mediaStrings.movies.deleteTitle}</p>
                <h2>{mediaStrings.movies.deletePrompt}</h2>
                <p>
                  {mediaStrings.movies.deleteDetail(moviePendingDelete.title)}
                  {' '}
                  {mediaStrings.library.detailsCannotUndo}
                </p>
                <div className="form-actions">
                  <button type="button" className="btn-ghost" onClick={() => setMoviePendingDelete(null)}>
                    {mediaStrings.library.cancel}
                  </button>
                  <button type="button" className="btn-danger solid" onClick={handleConfirmDelete} disabled={isDeleting}>
                    {isDeleting ? mediaStrings.library.deleting : mediaStrings.movies.deleteAction}
                  </button>
                </div>
              </div>
            </div>
          )}

          <section className="recipe-detail-hero movie-detail-hero">
            {selectedMovie.posterUrl ? (
              <img className="movie-detail-poster" src={selectedMovie.posterUrl} alt="" />
            ) : (
              <DefaultMediaFallback className="entry-detail-photo" fallbackType="movies" variant="detail-square" />
            )}
            <div className="movie-detail-copy">
              <p className="eyebrow">{selectedMovie.collection}</p>
              <h2>{selectedMovie.title}</h2>
              <p>
                {[selectedMovie.releaseYear, selectedMovie.director && mediaStrings.movies.directedBy(selectedMovie.director)]
                  .filter(Boolean)
                  .join(' - ')}
              </p>
            </div>
            {heroFacts.length > 0 && (
              <div className="recipe-detail-facts">
                {heroFacts.map(([label, value]) => (
                  <div key={label}>
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
            )}
          </section>

          <div className="recipe-detail-grid">
            <section className="recipe-detail-panel">
              <h3>{mediaStrings.movies.form.sections.library}</h3>
              <dl className="movie-info-list">
                {libraryRows.filter(([, value]) => value).map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <section className="recipe-detail-panel">
              <h3>{mediaStrings.movies.detailsTitle}</h3>
              <dl className="movie-info-list">
                {detailRows.filter(([, value]) => value).map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            </section>

            {selectedMovie.review && (
              <section className="recipe-detail-panel">
                <h3>{selectedMovie.spoiler ? mediaStrings.library.spoilers : mediaStrings.movies.reviewTitle}</h3>
                <p className="detail-notes">{selectedMovie.review}</p>
              </section>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="module-wrapper movies-module">
      {renderPremiumUpsell()}
      {showLetterboxdImport && (
        <LetterboxdImportModal
          remainingEntries={getRemainingEntries(subscription, 'movies')}
          usageReady={importUsageReady}
          usageError={!subscriptionLoading && !importUsageReady}
          onRetryUsage={() => { void refetchSubscription().catch(() => {}); }}
          onUpgrade={() => { setShowLetterboxdImport(false); setShowPremiumUpsell(true); }}
          collections={collections}
          error={isLimitReachedError(letterboxdPreviewError || letterboxdImportError) ? null : letterboxdPreviewError || letterboxdImportError}
          importResult={letterboxdImportResult}
          isImporting={isImportingLetterboxd}
          isPreviewing={isPreviewingLetterboxd}
          onClose={handleCloseLetterboxdImport}
          onImport={handleImportLetterboxd}
          onPreview={handlePreviewLetterboxd}
          preview={letterboxdPreviewData?.previewLetterboxdImport}
        />
      )}

      <div className="recipe-library-layout">
        <aside className="recipe-books-panel">
          <p className="eyebrow">{mediaStrings.library.watchlists}</p>
          <div className="recipe-book-list">
            <button type="button" className={!activeCollection ? 'active' : ''} onClick={() => setActiveCollection('')}>
              <span>{mediaStrings.movies.allItems}</span>
              <strong>{parsedMovies.length}</strong>
            </button>
            {collections.map((collection) => (
              <button
                key={collection}
                type="button"
                className={activeCollection === collection ? 'active' : ''}
                onClick={() => setActiveCollection(collection)}
              >
                <span>{collection}</span>
                <strong>{countByCollection(parsedMovies, collection)}</strong>
              </button>
            ))}
          </div>
        </aside>

        <section className="recipe-main-panel">
          <div className="recipe-filters">
            <div className="form-group search-field">
              <label>{mediaStrings.movies.addSearch.label}</label>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={mediaStrings.movies.searchPlaceholder}
              />
            </div>

            <div className="filter-row movie-filter-row">
              <label>
                {mediaStrings.library.status}
                <select value={activeStatus} onChange={(event) => setActiveStatus(event.target.value)}>
                  <option value="">{mediaStrings.library.anyStatus}</option>
                  {statuses.map((status) => <option key={status} value={status}>{status}</option>)}
                </select>
              </label>
              <label>
                {mediaStrings.library.genre}
                <select value={activeGenre} onChange={(event) => setActiveGenre(event.target.value)}>
                  <option value="">{mediaStrings.library.anyGenre}</option>
                  {genres.map((genre) => <option key={genre} value={genre}>{genre}</option>)}
                </select>
              </label>
              <button
                type="button"
                className={`favorite-filter ${favoritesOnly ? 'active' : ''}`}
                onClick={() => setFavoritesOnly((current) => !current)}
              >
                {mediaStrings.library.favorites}
              </button>
            </div>
          </div>

          {loading && parsedMovies.length === 0 && (
            <div className="module-loading-state">
              <ModuleListSkeleton />
            </div>
          )}
          {error && <p className="error-copy">Error: {getPublicErrorMessage(error)}</p>}

          <div className="recipe-stats">
            <span>{filteredMovies.length} shown</span>
            <span>{parsedMovies.filter((movie) => movie.favorite).length} favorites</span>
            <span>{collections.length} watchlists</span>
          </div>

          <div className="recipe-card-grid">
            {filteredMovies.map((movie) => (
              <button key={movie.id} type="button" className="recipe-card movie-card" onClick={() => handleSelectMovie(movie)}>
                <div className="movie-card-title">
                  {movie.posterUrl ? (
                    <img className="movie-poster-thumb" src={movie.posterUrl} alt="" />
                  ) : (
                    <DefaultMediaFallback className="entry-card-photo" fallbackType="movies" variant="thumbnail-square" />
                  )}
                  <div>
                    <div className="movie-card-heading">
                      <h3>{movie.title}</h3>
                      {movie.favorite && <span className="favorite-chip">{mediaStrings.library.favorites}</span>}
                    </div>
                    <p>{[movie.releaseYear, movie.director].filter(Boolean).join(' • ') || 'Movie details not added yet'}</p>
                    <p className="movie-card-detail-line">
                      {[movie.status, movie.collection, movie.whereWatched].filter(Boolean).join(' • ') || 'Library details not added yet'}
                    </p>
                  </div>
                </div>
                {movie.rating && <span className="movie-rating-pill" aria-label={`Rated ${movie.rating} out of 5`}>★ {movie.rating}</span>}
              </button>
            ))}
          </div>

          {moviesPageInfo.hasNextPage && (
            <div className="load-more-row">
              <button type="button" className="btn-secondary" onClick={handleLoadMore} disabled={loading}>
                {loading ? 'Loading movies...' : 'Load more movies'}
              </button>
            </div>
          )}

          {filteredMovies.length === 0 && !loading && (
            <HobbyEmptyState hobbyId="movies" filtered={parsedMovies.length > 0}
              title={parsedMovies.length === 0 ? mediaStrings.movies.emptyTitle : moduleCommonStrings.empty.noMatches}
              detail={parsedMovies.length === 0 ? mediaStrings.movies.emptyFirst : moduleCommonStrings.filters.tryDifferentSearchOrFilter} />
          )}
        </section>
      </div>
    </div>
  );
}
